<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/ops/dashboard">Ops</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Pre-applications</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Pre-applications</h1>
        @if (!isLoading()) {
          <p class="page-head__subtitle">
            {{ rows().length }} {{ isFiltered() ? 'shown' : 'in queue' }} · Authorised Ops
          </p>
        }
      </div>
      <div class="page-head__actions">
        <button
          mat-stroked-button
          type="button"
          (click)="toggleOrdering()"
          [matTooltip]="
            ordering() === '-submitted_at'
              ? 'Showing newest submissions first'
              : 'Showing oldest submissions first'
          "
        >
          <app-icon name="restore" [size]="18" />
          {{ ordering() === '-submitted_at' ? 'Newest first' : 'Oldest first' }}
        </button>
      </div>
    </div>
  </header>

  <div class="queue-filters" role="group" aria-label="Filter by review status">
    @for (filter of filters; track filter.value) {
      <button
        class="queue-filter"
        type="button"
        [class.queue-filter--active]="statusFilter() === filter.value"
        [attr.aria-pressed]="statusFilter() === filter.value"
        (click)="setFilter(filter.value)"
      >
        {{ filter.label }}
      </button>
    }
  </div>

  @if (isLoading()) {
    <mat-card appearance="outlined" class="table-card">
      <table class="queue-table sg-table--skeleton">
        <thead>
          <tr>
            <th>Submitted</th>
            <th>Business</th>
            <th>Submitted by</th>
            <th>Recommendation</th>
            <th>Documents</th>
            <th>Status</th>
            <th>Dealt with by</th>
          </tr>
        </thead>
        <tbody>
          @for (r of skeletonRows; track r) {
            <tr>
              <td><span class="sg-skeleton sg-skeleton--text" style="width: 65%"></span></td>
              <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span></td>
              <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span></td>
              <td><span class="sg-skeleton sg-skeleton--text" style="width: 55%"></span></td>
              <td><span class="sg-skeleton sg-skeleton--text" style="width: 30%"></span></td>
              <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
              <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w50"></span></td>
            </tr>
          }
        </tbody>
      </table>
    </mat-card>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load pre-applications</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <button mat-stroked-button type="button" (click)="reload()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </div>
  } @else if (rows().length === 0) {
    <div class="sg-empty sg-empty--bordered">
      <app-icon name="document" [size]="40" />
      @if (isFiltered()) {
        <p class="sg-empty__title">No results</p>
        <p class="sg-empty__desc">No pre-applications match this filter right now.</p>
        <button mat-stroked-button type="button" (click)="setFilter('ALL')">
          <app-icon name="restore" [size]="18" /> Clear filter
        </button>
      } @else {
        <p class="sg-empty__title">No pre-applications yet</p>
        <p class="sg-empty__desc">Pre-applications submitted for a decision will appear here.</p>
      }
    </div>
  } @else {
    <mat-card appearance="outlined" class="table-card">
      <table mat-table [dataSource]="pagedRows()" class="queue-table">
        <ng-container matColumnDef="entity">
          <th mat-header-cell *matHeaderCellDef>Business</th>
          <td mat-cell *matCellDef="let row">
            <span class="queue-table__entity">{{ row.entity_name }}</span>
            @if (row.documents_stale) {
              <!-- Advisory only (ruling Q6) — it never blocks a decision. -->
              <app-icon
                class="queue-table__stale"
                name="alert-triangle"
                [size]="15"
                matTooltip="Rests on documents older than 90 days"
                aria-label="Rests on documents older than 90 days"
              />
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="submitted_by">
          <th mat-header-cell *matHeaderCellDef>Submitted by</th>
          <td mat-cell *matCellDef="let row">
            <span>{{ row.submitted_by | dash }}</span>
            @if (row.agency_name) {
              <span class="queue-table__agency">{{ row.agency_name }}</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="recommendation">
          <th mat-header-cell *matHeaderCellDef>Recommendation</th>
          <td mat-cell *matCellDef="let row" class="queue-table__rec">
            {{ recommendationLabel(row.recommendation) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="documents">
          <th mat-header-cell *matHeaderCellDef>Documents</th>
          <td mat-cell *matCellDef="let row">
            {{ row.document_count }}
            @if (row.post_submission_document_count > 0) {
              <!-- Added by the client after submitting: the review may have
                   started without them. -->
              <span
                class="queue-table__late"
                [matTooltip]="row.post_submission_document_count + ' added after submission'"
                [attr.data-testid]="'queue-late-docs-' + row.short_id"
                >+{{ row.post_submission_document_count }} late</span
              >
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let row">
            <app-status-chip [status]="row.review_status" />
          </td>
        </ng-container>

        <ng-container matColumnDef="submitted">
          <th mat-header-cell *matHeaderCellDef>Submitted</th>
          <td mat-cell *matCellDef="let row" class="queue-table__submitted">
            <!--
              `medium` and not a hand-rolled pattern: DatePipe treats a NAMED
              format as one token, so "mediumDate 'at' HH:mm" is not "mediumDate
              plus a time" — every unquoted letter outside the quotes is read as
              a field symbol, which is how this rendered "48e22iu48DPMte at 17:48".
              `medium` is the project's date-with-time format (style guide:
              mediumDate in lists, medium with time in detail/timeline), and this
              column exists to carry the minute.
            -->
            {{ row.submitted_at | date: 'medium' | dash }}
          </td>
        </ng-container>

        <ng-container matColumnDef="assignee">
          <th mat-header-cell *matHeaderCellDef>Dealt with by</th>
          <td mat-cell *matCellDef="let row">
            @if (row.assignee_name) {
              {{ row.assignee_name }}
            } @else {
              <span class="queue-unassigned">Nobody</span>
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="columns"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: columns"
          class="queue-table__row"
          [routerLink]="['/ops/pre-applications', row.short_id]"
        ></tr>
      </table>

      @if (rows().length > pageSize()) {
        <mat-paginator
          [length]="rows().length"
          [pageSize]="pageSize()"
          [pageSizeOptions]="[25, 50, 100]"
          (page)="onPage($event)"
          aria-label="Select page"
        />
      }
    </mat-card>
  }
</div>
